import JSConfetti from 'js-confetti';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import useModal from 'hooks/useModal';

import { sendEvent } from 'lib/gtag';
import { getFromSessionStorage, setSessionStorage } from 'lib/sessionStorage';
import { sendSupixEvent } from 'lib/supix';

import { useUser } from 'providers/UserProvider';

import useDialog from './useDialog';
import useLocalStorageState from './useLocalStorageState';
import useMyBlackFridayScheduler from './useMyBlackFridayScheduler';

import LINKS from 'constants/links';
import { WISHLIST_TABS } from 'constants/wishlist';

const DownloadAppModal = dynamic(
  () => import('components/pages/MyBlackFriday/DownloadAppModal'),
  { ssr: false }
);

const LoginDialog = dynamic(() => import('components/LoginDialog'), {
  ssr: false,
});

const WishlistDialog = dynamic(
  () => import('components/Wishlist/WishlistDialog'),
  { ssr: false }
);

const WhatsAppVipGroupModal = dynamic(
  () => import('components/pages/MyBlackFriday/WhatsAppVipGroupModal'),
  { ssr: false }
);

const CompletedMissionsDialog = dynamic(
  () => import('components/pages/MyBlackFriday/CompletedMissionsDialog'),
  { ssr: false }
);

const LOCAL_STORAGE_KEY = 'promobit.bf-missions';
const GA_EVENT_CATEGORY = 'my_black_friday_page';
const FALLBACK_UTM_CAMPAIGN = 'minhabf';

// Uses random UUIDs to avoid conflicts if a mission is added/removed during
// Black-Friday period.
export const MISSIONS_IDS = {
  LOGIN: 'dcaf97c8-104f-4926-abb6-6d938f112ee3',
  WISHLIST: 'bc6c83bb-9a59-45c9-95a0-3a049450f1a2',
  COUPONS: '7098a7d4-c46d-4b59-9b7e-26a3ed156668',
  VIP: 'cf13e9c7-8f74-46c6-8e57-f300c2b131e8',
  DOWNLOAD_APP: 'cfd377a7-f890-4ba8-82cd-c9758fa5385b',
  PARTNER_DOWNLOAD_APP: 'c2b91d7e-2307-471a-b3e8-bcc8ee7be76e',
};

export const MISSIONS_STATUS = {
  DONE: 'done',
  UNLOCKED: 'unlocked',
  LOCKED: 'locked',
};

const CHECKPOINTS = [
  {
    label: 'Top 80%',
    type: 'Iniciante Econômico',
    dependsOn: MISSIONS_IDS.LOGIN,
  },
  {
    label: 'Top 50%',
    type: 'Caçador de Economia',
    dependsOn: MISSIONS_IDS.WISHLIST,
  },
  {
    label: 'Top 30%',
    type: 'Estrategista de Ofertas',
    dependsOn: MISSIONS_IDS.COUPONS,
  },
  {
    label: 'Top 10%',
    type: 'Expert em Economia',
    dependsOn: MISSIONS_IDS.VIP,
  },
  {
    label: 'Top 1%',
    type: 'Mestre da Economia',
    dependsOn: MISSIONS_IDS.DOWNLOAD_APP,
  },
];

const isDone = (missionsState, missionId) =>
  !!missionsState.find((mission) => mission.id === missionId)?.completedAt;

const getStatus = (missionsState, missionId) => {
  const mission = missionsState.find((m) => m.id === missionId);

  if (!mission || (!mission.completedAt && !mission.startedAt)) {
    return MISSIONS_STATUS.LOCKED;
  }

  if (mission.completedAt) {
    return MISSIONS_STATUS.DONE;
  }

  if (mission.startedAt) {
    return MISSIONS_STATUS.UNLOCKED;
  }
};

const useBlackFridayMissions = () => {
  const router = useRouter();
  const [confetti, setConfetti] = useState(null);
  const { showDialog } = useDialog();
  const { showModal } = useModal();
  const { isLoggedIn, loadingLoggedInUser } = useUser();
  const { getCampaignByType } = useMyBlackFridayScheduler();
  const partnerCampaign = getCampaignByType('PARTNER_APP');
  const [persistedMissions, setPersistedMissions] = useLocalStorageState(
    LOCAL_STORAGE_KEY,
    [
      {
        id: MISSIONS_IDS.LOGIN,
        startedAt: new Date().toUTCString(),
        completedAt: null,
      },
    ]
  );

  useEffect(() => {
    setConfetti(new JSConfetti());
  }, []);

  const markAsStarted = useCallback(
    (missionId) => {
      const now = new Date().toUTCString();

      setPersistedMissions((prevMissions) => {
        const mission = prevMissions.find((m) => m.id === missionId);

        if (!mission) {
          return [
            ...prevMissions,
            { id: missionId, startedAt: now, completedAt: null },
          ];
        }

        mission.startedAt = now;

        return [...prevMissions];
      });
    },
    [setPersistedMissions]
  );

  const markAsCompleted = useCallback(
    (missionId, { silent = false } = {}) => {
      const now = new Date().toUTCString();

      setPersistedMissions((prevMissions) => {
        const mission = prevMissions.find((m) => m.id === missionId);

        if (!mission || !mission.completedAt) {
          if (!silent) {
            confetti.addConfetti({
              confettiColors: ['#FFC934', '#0072ED'],
              emojiSize: 50,
            });
          }

          const card = document.querySelector(`[data-gameid="${missionId}"]`);

          if (card) {
            card.classList.add('mission-completed');

            setTimeout(() => {
              card.classList.remove('mission-completed');
            }, 1000);
          }
        }

        if (!mission) {
          return [
            ...prevMissions,
            { id: missionId, startedAt: now, completedAt: now },
          ];
        }

        mission.completedAt = now;

        return [...prevMissions];
      });
    },
    [setPersistedMissions]
  );

  const missions = useMemo(() => {
    const sendDoneEvent = ({ percentage, categoryId }) => {
      sendEvent({
        action: `click_mission_done`,
        category: GA_EVENT_CATEGORY,
        label: percentage,
      });
      sendSupixEvent('/car', { category: categoryId });
    };

    return [
      {
        id: MISSIONS_IDS.LOGIN,
        title: 'Faça seu cadastro no Promobit',
        description: 'É rápido e 100% gratuito',
        buttonLabel: 'Cadastrar',
        status: getStatus(persistedMissions, MISSIONS_IDS.LOGIN),
        shouldPersistAction: false,
        action: () => {
          showDialog(LoginDialog, {
            isMyBlackFridayGamePage: true,
            onSuccess: () => {
              sendDoneEvent({ percentage: '80', categoryId: 100010101 });
            },
          });
        },
      },
      {
        id: MISSIONS_IDS.WISHLIST,
        title: 'Atualize a sua Lista de Desejos',
        description: 'Realize seus sonhos pagando pouco',
        buttonLabel: 'Acessar a lista',
        status: getStatus(persistedMissions, MISSIONS_IDS.WISHLIST),
        shouldPersistAction: false,
        action: () => {
          showDialog(WishlistDialog, {
            suggestedTerms: [
              'Tênis',
              'Creatina',
              'Fone sem fio',
              'TV 4K',
              'Jogo PS5',
              'Console',
              'Notebook',
              'Perfume',
              'Ar condicionado',
            ],
            onCloseWithKeywordAdded: () => {
              markAsCompleted(MISSIONS_IDS.WISHLIST);
              markAsStarted(MISSIONS_IDS.COUPONS);
              sendDoneEvent({ percentage: '50', categoryId: 100010102 });
            },
          });
        },
      },
      {
        id: MISSIONS_IDS.COUPONS,
        title: 'Descubra cupons especiais',
        description: 'Aproveite descontos exclusivos',
        buttonLabel: 'Visualizar Cupons',
        status: getStatus(persistedMissions, MISSIONS_IDS.COUPONS),
        shouldPersistAction: false,
        action: () => {
          showDialog(WishlistDialog, {
            tabId: WISHLIST_TABS.COUPONS.id,
            suggestedCoupons: [
              {
                keywordStoreId: 572,
                keywordDisplay: 'Mercado Livre',
              },
              {
                keywordStoreId: 83,
                keywordDisplay: 'Amazon',
              },
              {
                keywordStoreId: 195,
                keywordDisplay: 'Aliexpress',
              },
              {
                keywordStoreId: 247,
                keywordDisplay: 'Kabum',
              },
              {
                keywordStoreId: 16,
                keywordDisplay: 'Magalu',
              },
              {
                keywordStoreId: 6,
                keywordDisplay: 'Casas Bahia',
              },
              {
                keywordStoreId: 515,
                keywordDisplay: 'Samsung',
              },
              {
                keywordStoreId: 8,
                keywordDisplay: 'Netshoes',
              },
              {
                keywordStoreId: 543,
                keywordDisplay: 'LG',
              },
              {
                keywordStoreId: 95,
                keywordDisplay: 'Carrefour',
              },
              {
                keywordStoreId: 10,
                keywordDisplay: 'Nike',
              },
            ],
            onCloseWithKeywordAdded: () => {
              markAsCompleted(MISSIONS_IDS.COUPONS);
              markAsStarted(MISSIONS_IDS.VIP);
              sendDoneEvent({ percentage: '30', categoryId: 100010103 });
            },
          });
        },
      },
      {
        id: MISSIONS_IDS.VIP,
        title: 'Grupo VIP',
        description: 'Saiba antes e economize mais',
        buttonLabel: 'Acessar grupo',
        status: getStatus(persistedMissions, MISSIONS_IDS.VIP),
        shouldPersistAction: true,
        action: () => {
          showModal(WhatsAppVipGroupModal, {
            onClose: () => {
              markAsCompleted(MISSIONS_IDS.VIP);
              markAsStarted(MISSIONS_IDS.DOWNLOAD_APP);
              markAsStarted(MISSIONS_IDS.PARTNER_DOWNLOAD_APP);
              sendDoneEvent({ percentage: '10', categoryId: 100010104 });

              setTimeout(() => {
                showDialog(CompletedMissionsDialog);
              }, 0);
            },
          });
        },
      },
    ];
  }, [
    loadingLoggedInUser,
    isLoggedIn,
    persistedMissions,
    showDialog,
    markAsCompleted,
    markAsStarted,
  ]);

  const bonusMissions = useMemo(() => {
    const sendDoneEvent = ({ index, title, categoryId }) => {
      sendEvent({
        action: `try_extra_${index}`,
        category: GA_EVENT_CATEGORY,
        label: title,
      });
      sendSupixEvent('/car', { category: categoryId });
    };

    const lastMissionStatus = isDone(persistedMissions, MISSIONS_IDS.VIP)
      ? MISSIONS_STATUS.UNLOCKED
      : MISSIONS_STATUS.LOCKED;

    const items = [
      {
        id: MISSIONS_IDS.DOWNLOAD_APP,
        title: 'Download do aplicativo Promobit',
        buttonLabel: 'Download',
        status: lastMissionStatus,
        shouldPersistAction: false,
        action: () => {
          sendEvent({
            action: 'click_extra_1',
            category: GA_EVENT_CATEGORY,
          });
          showModal(DownloadAppModal, {
            onClose: () => {
              markAsCompleted(MISSIONS_IDS.DOWNLOAD_APP);
              sendDoneEvent({
                index: '1',
                title: 'Download do aplicativo Promobit',
                categoryId: 100010105,
              });
            },
          });
        },
      },
    ];

    if (partnerCampaign) {
      items.push({
        id: MISSIONS_IDS.PARTNER_DOWNLOAD_APP,
        title: `Download do aplicativo ${partnerCampaign.name}`,
        buttonLabel: 'Download',
        href: partnerCampaign.url,
        status: lastMissionStatus,
        shouldPersistAction: false,
        action: () => {
          sendEvent({
            action: 'click_extra_2',
            category: GA_EVENT_CATEGORY,
          });
          sendDoneEvent({
            index: '2',
            title: `Download do aplicativo ${partnerCampaign.name}`,
            categoryId: 100010106,
          });
          markAsCompleted(MISSIONS_IDS.PARTNER_DOWNLOAD_APP);
        },
      });
    }

    return items;
  }, [persistedMissions, markAsCompleted, partnerCampaign]);

  const checkpoints = useMemo(
    () =>
      CHECKPOINTS.map(({ label, type, dependsOn }) => ({
        label,
        type,
        checked: isDone(persistedMissions, dependsOn),
      })),
    [persistedMissions]
  );

  useEffect(() => {
    let timeout;

    // Automatically marks "Login" mission as completed when user logs in
    if (!loadingLoggedInUser && isLoggedIn) {
      const isSavingGamePage =
        router.asPath.split('?')[0] !== LINKS.SAVING_GAME;

      timeout = setTimeout(() => {
        markAsCompleted(MISSIONS_IDS.LOGIN, {
          silent: isSavingGamePage,
        });
        markAsStarted(MISSIONS_IDS.WISHLIST);
      }, 500);
    }

    if (!getFromSessionStorage('utm_campaign')) {
      // Set UTM campaign to help us identify users that came from "Minha BF"
      // and your journey in our analytics tools.
      setSessionStorage('utm_campaign', FALLBACK_UTM_CAMPAIGN);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [loadingLoggedInUser, isLoggedIn, router]);

  return {
    bonusMissions,
    checkpoints,
    missions,
    markAsCompleted,
  };
};

export default useBlackFridayMissions;
